/**
 * Two-step dialog for sending an outbound message to a known contact.
 * Step 0: pick the integration (channel), then pick which of the contact's
 * identities to send to (filtered by the integration's recipient type), then
 * optionally an AI agent.
 * Step 1: reuses MessageAuthoring from the human-chat NewMessageModal so the
 * template / SMS / email composer behaves identically.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { CheckIcon, ChevronDownIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BrainVersionSelector } from '@/components/atoms/BrainVersionSelector';
import { IntegrationIcon } from '@/components/organisms/Integrations';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import { cn } from '@/lib/utils';
import { MessageAuthoring } from '@/modules/humanChat/components/NewMessageModal/MessageAuthoring';
import { NewMessageFooter } from '@/modules/humanChat/components/NewMessageModal/NewMessageFooter';
import { NoIntegrationsEmptyState } from '@/modules/humanChat/components/NewMessageModal/NoIntegrationsEmptyState';
import { useNewMessageForm } from '@/modules/humanChat/hooks/useNewMessageForm';
import { useNewMessageSend } from '@/modules/humanChat/hooks/useNewMessageSend';
import { useNewMessageTemplates } from '@/modules/humanChat/hooks/useNewMessageTemplates';
import { trackEvent } from '@/segment/segment';
import type { Contact, ContactIdentity } from '@/shared/types/contacts';
import { Integration, IntegrationStatus, IntegrationType } from '@/shared/types/integration';
import { EventName } from '@/shared/types/segment';
import {
  DIRECT_MESSAGE_INTEGRATION_TYPES,
  getConfigKey,
  getDisplayName,
  getRecipientType,
} from '@/util/outboundIntegrations';

interface SendMessageToContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: Contact;
  identities: ContactIdentity[];
}

const getIntegrationDisplayName = (integration: Integration): string => {
  const type = integration.type as IntegrationType;
  const configKey = getConfigKey(type);
  const sender = configKey ? (integration.config as Record<string, string>)?.[configKey] : null;
  return `${getDisplayName(type)} ${sender || integration.name}`;
};

const getIdentityLabel = (identity: ContactIdentity): string => identity.value;

export const SendMessageToContactDialog = ({
  open,
  onOpenChange,
  contact,
  identities,
}: SendMessageToContactDialogProps) => {
  const { t } = useTranslation();
  const deskId = useDeskId();

  const [step, setStep] = useState<0 | 1>(0);
  const [integrationPopoverOpen, setIntegrationPopoverOpen] = useState(false);
  const [selectedIdentityId, setSelectedIdentityId] = useState<string>('');

  const {
    form,
    resetForm,
    headerFields,
    bodyFields,
    footerFields,
    headerReplace,
    bodyReplace,
    footerReplace,
    onContentChange,
    onSubjectChange,
    onAiAgentChange,
  } = useNewMessageForm();

  const { control, setValue, clearErrors } = form;
  const selectedAiAgent = useWatch({ control, name: 'aiAgent' });
  const selectedIntegrationId = useWatch({ control, name: 'integrationId' });

  const { integrations: allIntegrations, listStatus: integrationsStatus } = useIntegrations(
    deskId || ''
  );
  const integrationsLoading = integrationsStatus === 'pending';

  const phoneIdentities = useMemo(
    () => identities.filter((identity) => identity.type === 'phone'),
    [identities]
  );
  const emailIdentities = useMemo(
    () => identities.filter((identity) => identity.type === 'email'),
    [identities]
  );

  // Only show integrations whose recipient type matches an identity the contact has.
  const integrations = useMemo(() => {
    if (!allIntegrations) {
      return [];
    }

    return allIntegrations.filter((integration) => {
      const type = integration.type as IntegrationType;
      if (!DIRECT_MESSAGE_INTEGRATION_TYPES.includes(type)) {
        return false;
      }
      if (!integration.active || integration.status !== IntegrationStatus.VERIFIED) {
        return false;
      }
      const recipientType = getRecipientType(type);
      if (recipientType === 'phone') {
        return phoneIdentities.length > 0;
      }
      if (recipientType === 'email') {
        return emailIdentities.length > 0;
      }
      return false;
    }) as Integration[];
  }, [allIntegrations, phoneIdentities.length, emailIdentities.length]);

  const selectedIntegration = useMemo<Integration | null>(() => {
    if (!selectedIntegrationId || !allIntegrations) {
      return null;
    }
    return (
      (allIntegrations.find((i) => i.integration_id === selectedIntegrationId) as Integration) ??
      null
    );
  }, [selectedIntegrationId, allIntegrations]);

  const selectedIntegrationType =
    (selectedIntegration?.type as IntegrationType | undefined) ?? null;

  // Identities valid for the currently picked integration's recipient type.
  const compatibleIdentities = useMemo(() => {
    if (!selectedIntegrationType) {
      return [];
    }
    const recipientType = getRecipientType(selectedIntegrationType);
    if (recipientType === 'phone') {
      return phoneIdentities;
    }
    if (recipientType === 'email') {
      return emailIdentities;
    }
    return [];
  }, [selectedIntegrationType, phoneIdentities, emailIdentities]);

  const selectedIdentity = useMemo(
    () => compatibleIdentities.find((identity) => identity.identity_id === selectedIdentityId),
    [compatibleIdentities, selectedIdentityId]
  );

  const writeIdentityToForm = (identity: ContactIdentity | undefined) => {
    if (!identity) {
      setValue('toPhone', '', { shouldValidate: true });
      setValue('toEmail', '', { shouldValidate: true });
      return;
    }
    if (identity.type === 'phone') {
      setValue('toPhone', identity.value, { shouldDirty: true, shouldValidate: true });
      setValue('toEmail', '', { shouldValidate: true });
    } else {
      setValue('toEmail', identity.value, { shouldDirty: true, shouldValidate: true });
      setValue('toPhone', '', { shouldValidate: true });
    }
  };

  const handleIdentityChange = (identityId: string) => {
    setSelectedIdentityId(identityId);
    writeIdentityToForm(compatibleIdentities.find((i) => i.identity_id === identityId));
  };

  const setIntegrationOnForm = (integration: Integration | null) => {
    setValue('integrationId', integration?.integration_id || '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('integrationType', (integration?.type as IntegrationType) ?? null, {
      shouldValidate: true,
    });
    setValue('templateId', null);
    setValue('headerParameters', []);
    setValue('bodyParameters', []);
    setValue('footerParameters', []);
    setValue('headerMediaParam', null);
    setValue('content', '');
    setValue('subject', '');
    clearErrors();
  };

  const handleIntegrationChange = (newIntegrationId: string) => {
    if (newIntegrationId === selectedIntegrationId) {
      return;
    }
    const newIntegration =
      allIntegrations?.find((i) => i.integration_id === newIntegrationId) ?? null;
    setIntegrationOnForm(newIntegration as Integration | null);

    const previousType = selectedIntegrationType ? getRecipientType(selectedIntegrationType) : null;
    const newType = newIntegration
      ? getRecipientType(newIntegration.type as IntegrationType)
      : null;
    if (previousType !== newType) {
      setSelectedIdentityId('');
      writeIdentityToForm(undefined);
    }
  };

  // One-time bootstrap effects: pre-select the only available integration /
  // identity once the data finishes loading. Using effects (rather than a
  // render-time derivation) keeps the form/state as the single source of truth
  // for downstream hooks (templates, send, validation).
  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler,react-you-might-not-need-an-effect/no-chain-state-updates */
  useEffect(() => {
    if (selectedIntegrationId || integrationsLoading || integrations.length !== 1) {
      return;
    }
    setIntegrationOnForm(integrations[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrations, integrationsLoading, selectedIntegrationId]);

  useEffect(() => {
    if (selectedIdentityId || compatibleIdentities.length !== 1) {
      return;
    }
    handleIdentityChange(compatibleIdentities[0].identity_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compatibleIdentities, selectedIdentityId]);
  /* eslint-enable react-you-might-not-need-an-effect/no-event-handler,react-you-might-not-need-an-effect/no-chain-state-updates */

  const { templates, isTemplatesLoading, selectedTemplate, ...templateRest } =
    useNewMessageTemplates({
      form,
      selectedIntegration,
      selectedIntegrationType,
      headerReplace,
      bodyReplace,
      footerReplace,
    });

  const contactContext = useMemo(
    () => ({
      userName: contact.name ?? null,
      sessionCount: 0,
      lastSessionDate: '',
      lastSessionId: '',
    }),
    [contact.name]
  );

  const { sendMessage, isSending } = useNewMessageSend({
    form,
    selectedIntegration,
    selectedIntegrationType,
    selectedTemplate,
    contactContext,
  });

  // Reset dialog state when it (re)opens — not on close — so the user doesn't
  // see the form revert to step 0 during Radix's close animation.
  /* eslint-disable react-you-might-not-need-an-effect/no-adjust-state-on-prop-change */
  useEffect(() => {
    if (open) {
      resetForm();
      setSelectedIdentityId('');
      setStep(0);
    }
    // resetForm is stable from react-hook-form; intentionally only depend on `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  /* eslint-enable react-you-might-not-need-an-effect/no-adjust-state-on-prop-change */

  const closeDialog = () => {
    onOpenChange(false);
  };

  const onNext = async () => {
    const valid = await form.trigger(['integrationId', 'integrationType', 'toPhone', 'toEmail']);
    if (!valid || !selectedIdentity) {
      return;
    }

    trackEvent(EventName.ClickNewMessageNext, {
      integration_type: selectedIntegrationType ?? undefined,
    });

    if (
      selectedIntegrationType === IntegrationType.WHATSAPP &&
      templates.length > 0 &&
      !form.getValues('templateId')
    ) {
      templateRest.onTemplateChange(templates[0].id);
    }

    setStep(1);
  };

  const onBack = () => setStep(0);

  const isSendingRef = useRef(false);
  const onSend = async () => {
    if (isSendingRef.current) {
      return;
    }
    const valid = await form.trigger();
    if (!valid) {
      return;
    }
    isSendingRef.current = true;
    try {
      const success = await sendMessage();
      if (success) {
        closeDialog();
      }
    } finally {
      isSendingRef.current = false;
    }
  };

  const dialogMaxWidth = step === 1 ? 'sm:max-w-screen-md' : 'sm:max-w-xl';
  const hasNoIntegrations = !integrationsLoading && integrations.length === 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          closeDialog();
        }
      }}
    >
      <DialogContent className={cn('max-h-[90vh] overflow-y-auto', dialogMaxWidth)}>
        <Form {...form}>
          {step === 0 && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {t('contacts.send_message.title', { 0: contact.name ?? t('contacts.unnamed') })}
                </DialogTitle>
                <DialogDescription>{t('contacts.send_message.subtitle')}</DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-4 px-1">
                {hasNoIntegrations ? (
                  <NoIntegrationsEmptyState onClose={closeDialog} />
                ) : (
                  <>
                    <FormField
                      control={control}
                      name="integrationId"
                      render={({ fieldState }) => (
                        <FormItem>
                          <FormLabel>{t('conversation.new_message.send_from')}</FormLabel>
                          <Popover
                            open={integrationPopoverOpen}
                            onOpenChange={setIntegrationPopoverOpen}
                          >
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline-neutral"
                                role="combobox"
                                aria-expanded={integrationPopoverOpen}
                                disabled={integrationsLoading}
                                className="h-10 w-full justify-between hover:bg-transparent"
                              >
                                {integrationsLoading && <Skeleton className="h-4 w-40" />}
                                {!integrationsLoading && selectedIntegration && (
                                  <div className="flex items-center gap-2 font-normal text-foreground">
                                    <IntegrationIcon
                                      type={selectedIntegration.type as IntegrationType}
                                      size={20}
                                    />
                                    <span>{getIntegrationDisplayName(selectedIntegration)}</span>
                                  </div>
                                )}
                                {!integrationsLoading && !selectedIntegration && (
                                  <span className="font-normal text-muted-foreground">
                                    {t('conversation.new_message.send_from_placeholder')}
                                  </span>
                                )}
                                <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="z-1300 w-(--radix-popover-trigger-width) p-0"
                              align="start"
                            >
                              <Command>
                                <CommandInput placeholder={t('common.search')} />
                                <CommandList>
                                  <CommandEmpty>
                                    {integrationsLoading
                                      ? t('common.loading')
                                      : t('common.no_integrations')}
                                  </CommandEmpty>
                                  <CommandGroup>
                                    {integrations.map((integration) => (
                                      <CommandItem
                                        key={integration.integration_id}
                                        value={getIntegrationDisplayName(integration)}
                                        onSelect={() => {
                                          trackEvent(EventName.SelectNewMessageIntegration, {
                                            integration_type: integration.type,
                                          });
                                          handleIntegrationChange(integration.integration_id || '');
                                          setIntegrationPopoverOpen(false);
                                        }}
                                      >
                                        <div className="flex flex-1 items-center gap-2">
                                          <IntegrationIcon
                                            type={integration.type as IntegrationType}
                                            size={20}
                                          />
                                          <span>{getIntegrationDisplayName(integration)}</span>
                                        </div>
                                        <CheckIcon
                                          className={cn(
                                            'ml-auto h-4 w-4',
                                            selectedIntegration?.integration_id ===
                                              integration.integration_id
                                              ? 'opacity-100'
                                              : 'opacity-0'
                                          )}
                                        />
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                          {fieldState.error?.message && (
                            <p className="text-sm text-warning">{t(fieldState.error.message)}</p>
                          )}
                        </FormItem>
                      )}
                    />

                    {selectedIntegrationType && (
                      <FormItem>
                        <FormLabel>{t('conversation.new_message.to')}</FormLabel>
                        <Select
                          value={selectedIdentityId}
                          onValueChange={handleIdentityChange}
                          disabled={compatibleIdentities.length === 0}
                        >
                          <SelectTrigger className="h-10 w-full">
                            <SelectValue
                              placeholder={t('contacts.send_message.identity_placeholder')}
                            />
                          </SelectTrigger>
                          <SelectContent className="z-1300">
                            {compatibleIdentities.map((identity) => (
                              <SelectItem key={identity.identity_id} value={identity.identity_id}>
                                <span className="flex items-center gap-2">
                                  {identity.type === 'phone' ? (
                                    <PhoneIcon className="h-4 w-4 text-muted-foreground" />
                                  ) : (
                                    <MailIcon className="h-4 w-4 text-muted-foreground" />
                                  )}
                                  {getIdentityLabel(identity)}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}

                    <FormField
                      control={control}
                      name="aiAgent"
                      render={() => (
                        <FormItem>
                          <FormLabel>{t('conversation.new_message.ai_agent')}</FormLabel>
                          <BrainVersionSelector
                            onSelect={onAiAgentChange}
                            initialBrainId={selectedAiAgent?.brain_parent_id}
                            initialVersion={selectedAiAgent?.brain_version}
                            contentClassName="z-1300"
                            placeholder={t('common.none')}
                          />
                          <FormDescription>
                            {t('conversation.new_message.ai_agent_helper')}
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </>
                )}
              </div>
            </>
          )}

          {step === 1 && selectedIntegrationType && selectedIdentity && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {t('conversation.new_message.title_step1', {
                    0: contact.name?.trim() || selectedIdentity.value,
                  })}
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-4 overflow-y-auto px-1 pb-1">
                <MessageAuthoring
                  selectedIntegrationType={selectedIntegrationType}
                  templates={templates}
                  isTemplatesLoading={isTemplatesLoading}
                  selectedTemplate={selectedTemplate}
                  headerFields={headerFields}
                  bodyFields={bodyFields}
                  footerFields={footerFields}
                  headerVariablesCount={templateRest.headerVariablesCount}
                  bodyVariablesCount={templateRest.bodyVariablesCount}
                  footerVariablesCount={templateRest.footerVariablesCount}
                  onTemplateChange={templateRest.onTemplateChange}
                  onContentChange={onContentChange}
                  onSubjectChange={onSubjectChange}
                  hideAudienceVariablesHint
                />
              </div>
            </>
          )}
        </Form>
        {!hasNoIntegrations && (
          <NewMessageFooter
            isSending={isSending}
            step={step}
            selectedIntegrationType={selectedIntegrationType ?? undefined}
            isNextLoading={false}
            isNewContact={false}
            onNext={onNext}
            onBack={onBack}
            onSend={onSend}
            onCloseAttempt={closeDialog}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
